import { FormsModule } from '@angular/forms';
import { Component, Input, signal, inject, OnInit, OnChanges, OnDestroy, SimpleChanges, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ProjectDocService } from '../../../../core/services/project-doc.service';
import { ProjectDoc, ProjectDocReferential } from '../../../../core/models/models';
import { ProjectDocPreviewDialogComponent } from './project-doc-preview-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmationDialogComponent } from '../../../../shared/components/dialogs/confirmation-dialog.component';
import { DocTypeVersionService } from '../../../../core/services/doc-type-version.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';


@Component({
  selector: 'app-project-documents',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, ProjectDocPreviewDialogComponent, ConfirmationDialogComponent, MatProgressBarModule],

  templateUrl: './project-documents.component.html',
  styleUrl: './project-documents.component.scss'
})
export class ProjectDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  private projectDocService = inject(ProjectDocService);
  private docTypeVersionService = inject(DocTypeVersionService);

  @Input({ required: true }) projectId!: number;
  @Input() isProjectLocked = false;
  @Input() readonlyMode = false;

  error = output<string>();

  referentials = signal<ProjectDocReferential[]>([]);
  projectDocs = signal<ProjectDoc[]>([]);
  isUploading = signal<number | null>(null);
  previewDoc = signal<ProjectDoc | null>(null);
  deleteConfirmDocId = signal<number | null>(null);
  successMessage = signal('');
  localErrorMessage = signal('');

  draggedOverReferentialId = signal<number | null>(null);

  // Debounce subjects — une Map par docId pour comment et docReference
  private commentSubjects = new Map<number, Subject<string>>();
  private referenceSubjects = new Map<number, Subject<string>>();
  private debounceSubscriptions = new Subscription();

  ngOnDestroy(): void {
    this.debounceSubscriptions.unsubscribe();
  }

  ngOnInit(): void {
    if (this.projectId) {
      this.loadData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      this.loadData();
    }
  }

  private loadData(): void {
    forkJoin({
      validated: this.docTypeVersionService.getValidatedReferentials(),
      docs: this.projectDocService.getDocsByProject(this.projectId)
    }).subscribe({
      next: ({ validated, docs }) => {
        this.projectDocs.set(docs);

        // Ajouter les référentiels orphelins (liés à des docs mais absents du référentiel validé)
        const validatedIds = new Set(validated.map(r => r.id));
        const seen = new Set<number>();
        const orphans: ProjectDocReferential[] = docs
          .filter(d => !validatedIds.has(d.referentialId))
          .filter(d => { if (seen.has(d.referentialId)) return false; seen.add(d.referentialId); return true; })
          .map(d => ({ id: d.referentialId, name: d.referentialName, archived: true, maxDocs: undefined, docTypes: [] } as unknown as ProjectDocReferential));

        this.referentials.set([...validated, ...orphans]);
      }
    });
  }

  getDocsByReferential(referentialId: number): ProjectDoc[] {
    return this.projectDocs().filter(d => d.referentialId === referentialId);
  }

  shouldDisplayReferential(ref: ProjectDocReferential): boolean {
    return !ref.archived || this.getDocsByReferential(ref.id!).length > 0;
  }

  openFileDialog(input: HTMLInputElement, referentialId: number): void {
    if (this.isProjectLocked) {
      this.emitLockedMessage();
      return;
    }

    if (this.isUploading() === referentialId) {
      return;
    }
    const referential = this.referentials().find(r => r.id === referentialId);
    if (referential?.archived) {
      const message = 'Ce type de document est archivé. Import impossible, mais les documents existants restent conservés.';
      this.localErrorMessage.set(message);
      this.error.emit(message);
      return;
    }
    input.click();
  }

  onFileSelected(event: any, referentialId: number): void {
    if (this.isProjectLocked) {
      this.emitLockedMessage();
      return;
    }

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.uploadFile(file, referentialId, () => {
        input.value = '';
      });
    }
  }

  onDragOver(event: DragEvent, referentialId: number): void {
    if (this.readonlyMode || this.isProjectLocked) return;

    const referential = this.referentials().find(r => r.id === referentialId);
    if (referential?.archived || (referential?.maxDocs && this.getDocsByReferential(referentialId).length >= referential.maxDocs)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.draggedOverReferentialId.set(referentialId);
  }

  onDragLeave(event: DragEvent): void {
    if (this.readonlyMode || this.isProjectLocked) return;
    event.preventDefault();
    event.stopPropagation();
    this.draggedOverReferentialId.set(null);
  }

  onDrop(event: DragEvent, referentialId: number): void {
    if (this.readonlyMode || this.isProjectLocked) return;

    const referential = this.referentials().find(r => r.id === referentialId);
    if (referential?.archived || (referential?.maxDocs && this.getDocsByReferential(referentialId).length >= referential.maxDocs)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.draggedOverReferentialId.set(null);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.uploadFile(files[0], referentialId);
    }
  }

  private uploadFile(file: File, referentialId: number, callback?: () => void): void {
    if (file && this.projectId) {
      this.isUploading.set(referentialId);
      this.localErrorMessage.set('');
      this.projectDocService.uploadDoc(this.projectId, referentialId, file).subscribe({
        next: () => {
          this.loadData();
          this.successMessage.set('Document importé avec succès.');
          this.isUploading.set(null);
          if (callback) callback();
        },
        error: (err) => {
          const message = err?.error?.message || err?.error || "Erreur lors de l'upload";
          this.localErrorMessage.set(message);
          this.error.emit(message);
          this.isUploading.set(null);
          if (callback) callback();
        }
      });
    }
  }

  onDeleteDoc(docId: number): void {
    if (this.isProjectLocked) {
      this.emitLockedMessage();
      return;
    }
    this.deleteConfirmDocId.set(docId);
  }

  confirmDelete(): void {
    if (this.isProjectLocked) {
      this.emitLockedMessage();
      this.deleteConfirmDocId.set(null);
      return;
    }

    const docId = this.deleteConfirmDocId();
    if (docId) {
      this.projectDocService.deleteDoc(docId).subscribe({
        next: () => {
          this.loadData();
          this.successMessage.set('Document supprime avec succes.');
          this.deleteConfirmDocId.set(null);
        },
        error: (err) => {
          const message = err?.error?.message || err?.error || 'Erreur lors de la suppression du document';
          this.localErrorMessage.set(message);
          this.error.emit(message);
          this.deleteConfirmDocId.set(null);
        }
      });
    }
  }

  cancelDelete(): void {
    this.deleteConfirmDocId.set(null);
  }

  onDownloadDoc(doc: ProjectDoc): void {
    this.projectDocService.downloadDoc(doc.id!).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
      error: () => this.error.emit("Erreur lors du téléchargement")
    });
  }

  onPreviewDoc(doc: ProjectDoc): void {
    this.previewDoc.set(doc);
  }

  closePreview(): void {
    this.previewDoc.set(null);
  }

  onCommentChange(doc: ProjectDoc, comment: string): void {
    if (this.isProjectLocked || this.readonlyMode) return;

    // Mettre à jour la valeur locale immédiatement pour que l'UI reste réactive
    doc.comment = comment;

    if (!this.commentSubjects.has(doc.id!)) {
      const subject = new Subject<string>();
      this.commentSubjects.set(doc.id!, subject);
      this.debounceSubscriptions.add(
        subject.pipe(debounceTime(800)).subscribe(value => {
          this.projectDocService.updateDocComment(doc.id!, value).subscribe({
            next: (updated) => {
              const docs = this.projectDocs();
              const index = docs.findIndex(d => d.id === updated.id);
              if (index > -1) {
                docs[index] = updated;
                this.projectDocs.set([...docs]);
              }
            },
            error: (err) => {
              const message = err?.error?.message || err?.error || 'Erreur lors de la mise à jour du commentaire';
              this.localErrorMessage.set(message);
              this.error.emit(message);
            }
          });
        })
      );
    }
    this.commentSubjects.get(doc.id!)!.next(comment);
  }

  onDocReferenceChange(doc: ProjectDoc, docReference: string): void {
    if (this.isProjectLocked || this.readonlyMode) return;

    // Mettre à jour la valeur locale immédiatement pour que l'UI reste réactive
    doc.docReference = docReference;

    if (!this.referenceSubjects.has(doc.id!)) {
      const subject = new Subject<string>();
      this.referenceSubjects.set(doc.id!, subject);
      this.debounceSubscriptions.add(
        subject.pipe(debounceTime(800)).subscribe(value => {
          this.projectDocService.updateDocReference(doc.id!, value).subscribe({
            next: (updated) => {
              const docs = this.projectDocs();
              const index = docs.findIndex(d => d.id === updated.id);
              if (index > -1) {
                docs[index] = updated;
                this.projectDocs.set([...docs]);
              }
            },
            error: (err) => {
              const message = err?.error?.message || err?.error || 'Erreur lors de la mise à jour de la référence';
              this.localErrorMessage.set(message);
              this.error.emit(message);
            }
          });
        })
      );
    }
    this.referenceSubjects.get(doc.id!)!.next(docReference);
  }

  private emitLockedMessage(): void {
    const message = "Le projet est verrouille (VALIDE/TERMINE). L'ajout et la suppression de documents sont bloques.";
    this.localErrorMessage.set(message);
    this.error.emit(message);
  }
}
